import { cssBundleHref } from '@remix-run/css-bundle';
import type { LinksFunction, LoaderFunctionArgs } from '@remix-run/node';
import stylesheetUrl from '~/css/tailwind.css?url';
import fontStyleSheetUrl from '~/css/fonts.css?url';
import dropdownMenuStylesheet from '~/css/dropdownMenuStyles.css?url';
import { Link, Links, Meta, Outlet, Scripts, ScrollRestoration, useLoaderData, useRouteError } from '@remix-run/react';
import ProgressBar from './components/progressBar/ProgressBar';
import React from 'react';
import { Toaster } from './components/ui/toaster';
import { getEnvironment } from './utils/getEnvironment';
import { segmentWriteKeys } from './constants/segment';
import useLoadSegment from './analytics/useLoadSegment';
import useSegmentPageView from './analytics/useSegmentPageView';
import { tallyIds } from './constants/tallyId';
import useTallyId from './hooks/useTallyId';
import { ExternalScripts, ExternalScriptsHandle } from 'remix-utils/external-scripts';
import { PageRoutes } from './constants/routes';

export const links: LinksFunction = () => [
  ...(cssBundleHref ? [{ rel: 'stylesheet', href: cssBundleHref }] : []),
  { rel: 'stylesheet', href: stylesheetUrl },
  { rel: 'stylesheet', href: fontStyleSheetUrl },
  { rel: 'stylesheet', href: dropdownMenuStylesheet },
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  { rel: 'preconnect', href: 'https://fonts.gstatic.com' },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap',
  },
];

export const handle: ExternalScriptsHandle = {
  scripts: [{ src: 'https://tally.so/widgets/embed.js', async: true }],
};

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const environment = getEnvironment(url.hostname);
  const key = segmentWriteKeys[environment];
  const tallyId = tallyIds[environment];
  return { key, tallyId };
};

export function Layout({ children }: { children: React.ReactNode }) {
  const { key, tallyId } = useLoaderData<typeof loader>();
  useLoadSegment({ key });
  useSegmentPageView();
  useTallyId(tallyId);

  return (
    <html lang="en">
      <head>
        <Meta />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <style data-fullcalendar />
        <Links />
      </head>
      <body id="app">
        {children}
        <ScrollRestoration />
        <Scripts />
        <ExternalScripts />
        <ProgressBar />
        <Toaster />
      </body>
    </html>
  );
}

export default function App() {
  return <Outlet />;
}

export function ErrorBoundary() {
  const error = useRouteError();
  console.error(error);

  return (
    <html lang="en">
      <head>
        <Meta />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <style data-fullcalendar />
        <Links />
      </head>
      <body id="app">
        <h1 className="flex items-center justify-center">
          Something went wrong - please refresh the page or <Link to={PageRoutes.TEMPLATES}>click here</Link>
        </h1>
        <ScrollRestoration />
        <Scripts />
        <ExternalScripts />
        <ProgressBar />
        <Toaster />
      </body>
    </html>
  );
}
